import React, { useState, useEffect } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import Img from "components/img";

import Text from "components/Text";
import Icon from "components/Icon";
import Modal from "components/Modal";

import { colors, sizes, strings } from "assets/constants";
import { useFlag } from "hooks";

import * as ImagePicker from "expo-image-picker";
import { ImageEditor as CropView } from "expo-crop-image";
import { sn } from "utils";
import tinycolor from "tinycolor2";

interface Props {
  avatar?: AvatarType;
  onChange?: (avatar: AvatarType | undefined) => void;
}

export default function AvatarPicker(props: Props) {
  const { onChange } = props;
  const { flg: menu, on: showMenu, off: hideMenu } = useFlag();
  const { flg: cropper, on: showCropper, off: hideCropper } = useFlag();

  const [originalPic, setOriginalPic] = useState<AvatarType | undefined>();
  const [avatar, setAvatar] = useState<AvatarType | undefined>(props?.avatar);

  useEffect(() => setAvatar(props.avatar), [props.avatar]);

  const options: ImagePicker.ImagePickerOptions = {
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
  };

  const imagePickerCallback = (res: ImagePicker.ImagePickerResult) => {
    if (res?.canceled) {
      return;
    }

    const pics = res?.assets;
    pics &&
      setOriginalPic({
        uri: pics[0].uri,
        type: pics[0].type,
        name: pics[0].fileName,
      });
    showCropper();
  };

  const _pickFromCamera = () => {
    hideMenu();
    ImagePicker.launchCameraAsync(options).then(imagePickerCallback);
  };

  const _pickFromGallery = () => {
    hideMenu();
    ImagePicker.launchImageLibraryAsync(options).then(imagePickerCallback);
  };

  const _onSetCroppedImageToAvatar = (args: { uri?: string }) => {
    const { uri } = args;
    const newAvatar = { ...originalPic, uri };
    setAvatar(newAvatar);
    onChange && onChange(newAvatar);
    hideCropper();
  };

  return (
    <>
      <TouchableOpacity
        onPress={showMenu}
        style={styles.avatarContainer}
        activeOpacity={sizes.tapOpacity}
      >
        <Icon name="user" color="#e1e1e1" size={40} />
        <Img
          resizeMode={"contain"}
          source={{ uri: avatar?.uri }}
          style={styles.avatar}
        />
      </TouchableOpacity>

      <Modal hideCloseBtn hideToast visible={menu} close={hideMenu}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-around",
            paddingVertical: 30,
          }}
        >
          <TouchableOpacity
            style={{
              ...styles.pickButton,
              backgroundColor: tinycolor(colors.info).lighten(60).toString(),
            }}
            activeOpacity={sizes.tapOpacity}
            onPress={_pickFromCamera}
          >
            <Icon size={30} name="camera" color={colors.info} />
            <Text
              style={{
                marginTop: sn(
                  sizes.space / 2,
                  sizes.space / 2,
                  sizes.space,
                  sizes.space
                ),
              }}
              weight="bold"
              size={sn("lg", "lg", "xl", "xl")}
            >
              {strings.camera}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={{
              ...styles.pickButton,
              backgroundColor: tinycolor(colors.success).lighten(53).toString(),
            }}
            activeOpacity={sizes.tapOpacity}
            onPress={_pickFromGallery}
          >
            <Icon size={30} name="image" color={colors.success} />
            <Text
              style={{
                marginTop: sn(
                  sizes.space / 2,
                  sizes.space / 2,
                  sizes.space,
                  sizes.space
                ),
              }}
              weight="bold"
              size={sn("lg", "lg", "xl", "xl")}
            >
              {strings.gallery}
            </Text>
          </TouchableOpacity>
        </View>
      </Modal>

      {originalPic?.uri && CropView !== null && (
        <CropView
          isVisible={cropper}
          fixedAspectRatio={1}
          imageUri={originalPic?.uri}
          onEditingCancel={hideCropper}
          onEditingComplete={_onSetCroppedImageToAvatar}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  avatarContainer: {
    width: 100,
    aspectRatio: 1,
    alignSelf: "center",
    marginBottom: 30,
    borderRadius: 100,
    alignItems: "center",
    justifyContent: "center",
    elevation: 4,
    borderWidth: 3,
    borderColor: colors.border,
    backgroundColor: colors.lightButtons,
  },

  avatar: {
    width: "100%",
    height: "100%",
    position: "absolute",
    borderRadius: 100,
  },

  pickButton: {
    width: sn(100, 100, 150, 150),
    height: sn(100, 100, 150, 150),
    alignItems: "center",
    justifyContent: "center",
    borderRadius: sizes.radius.xxxl,
  },
});
